import type { PlannedSession, SessionType } from "@runlens/shared";
import { WorkoutSegments } from "./WorkoutSegments";
import {
  BoltIcon,
  FireIcon,
  HeartIcon,
  SparklesIcon,
  SunIcon,
  MoonIcon,
  XMarkIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";

interface SessionDetailModalProps {
  session: PlannedSession;
  onClose: () => void;
}

const sessionTypeConfig: Record<
  SessionType,
  { icon: typeof BoltIcon; label: string; color: string; bgColor: string }
> = {
  easy: {
    icon: SunIcon,
    label: "Easy Run",
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  long: {
    icon: SparklesIcon,
    label: "Long Run",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  tempo: {
    icon: FireIcon,
    label: "Tempo Run",
    color: "text-orange-600",
    bgColor: "bg-orange-100",
  },
  intervals: {
    icon: BoltIcon,
    label: "Intervals",
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  recovery: {
    icon: HeartIcon,
    label: "Recovery Run",
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  rest: {
    icon: MoonIcon,
    label: "Rest Day",
    color: "text-gray-500",
    bgColor: "bg-gray-100",
  },
};

const statusConfig: Record<
  string,
  { icon: typeof CheckCircleIcon; label: string; color: string; bgColor: string }
> = {
  planned: {
    icon: ClockIcon,
    label: "Planned",
    color: "text-gray-600",
    bgColor: "bg-gray-100",
  },
  completed: {
    icon: CheckCircleIcon,
    label: "Completed",
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  missed: {
    icon: XCircleIcon,
    label: "Missed",
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  skipped: {
    icon: ExclamationCircleIcon,
    label: "Skipped",
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
  },
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

function formatDistance(meters: number): string {
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function SessionDetailModal({ session, onClose }: SessionDetailModalProps) {
  const typeConfig = sessionTypeConfig[session.sessionType];
  const TypeIcon = typeConfig.icon;
  const status = statusConfig[session.status] || statusConfig.planned;
  const StatusIcon = status.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header with session type */}
        <div className={`px-6 py-4 ${typeConfig.bgColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-white/50 rounded-lg`}>
                <TypeIcon className={`w-6 h-6 ${typeConfig.color}`} />
              </div>
              <div>
                <h2 className={`text-xl font-semibold ${typeConfig.color}`}>
                  {typeConfig.label}
                </h2>
                <p className="text-sm text-gray-600">
                  {formatDate(session.scheduledDate)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-white/50 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Status badge */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${status.bgColor} ${status.color}`}>
              <StatusIcon className="w-4 h-4" />
              {status.label}
            </span>
          </div>

          {/* Targets */}
          {(session.targetDuration || session.targetDistance || session.targetZone) && (
            <div className="grid grid-cols-3 gap-4">
              {session.targetDuration && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <ClockIcon className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDuration(session.targetDuration)}
                  </p>
                  <p className="text-xs text-gray-500">Duration</p>
                </div>
              )}
              {session.targetDistance && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <MapPinIcon className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDistance(session.targetDistance)}
                  </p>
                  <p className="text-xs text-gray-500">Distance</p>
                </div>
              )}
              {session.targetZone && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <HeartIcon className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-lg font-semibold text-gray-900">
                    Zone {session.targetZone}
                  </p>
                  <p className="text-xs text-gray-500">HR Zone</p>
                </div>
              )}
            </div>
          )}

          {/* Workout structure or Description */}
          {session.workout && session.workout.length > 0 ? (
            <WorkoutSegments segments={session.workout} />
          ) : session.description ? (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {session.description}
              </p>
            </div>
          ) : null}

          {/* Matched activity info */}
          {session.matchedActivityId && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Matched to activity
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
